import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'https://api.pixlybooth.com';

export default function EventCompletionModal({ event, onClose, onCompleted }) {
  const [actualHours, setActualHours] = useState(
    event.scheduled_hours || event.duration_hours || 3
  );
  const [notes, setNotes] = useState('');
  const [checklistConfirmed, setChecklistConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hours = parseFloat(actualHours);
    if (isNaN(hours) || hours <= 0) {
      setError('Please specify valid actual hours worked (e.g. 3.5)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await axios.post(
        `${API}/api/organizer/events/${event.id}/complete`,
        {
          actual_hours_worked: hours,
          organizer_notes: notes.trim(),
          checklist: {
            boothSetup: true,
            testPrint: true,
            clientSignoff: checklistConfirmed,
          },
        },
        { withCredentials: true }
      );

      onCompleted && onCompleted(res.data.booking);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(5, 7, 15, 0.85)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '540px',
          background: 'linear-gradient(145deg, #121832 0%, #0c1022 100%)',
          borderRadius: '20px',
          border: '1px solid rgba(159, 92, 255, 0.35)',
          boxShadow: '0 25px 60px rgba(0,0,0,0.7), 0 0 40px rgba(159, 92, 255, 0.15)',
          overflow: 'hidden',
          animation: 'fadeInScale 0.25s ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(159, 92, 255, 0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 24 }}>🏁</span>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, color: '#fff', fontWeight: 700 }}>
                Wrap Up & Complete Event
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#a0a0b8' }}>
                Booking #{event.id} • {event.venue_name || 'Venue'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: 'none',
              borderRadius: '50%',
              width: 32,
              height: 32,
              color: '#a0a0b8',
              fontSize: 16,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>

        {/* Content & Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {error && (
            <div
              style={{
                background: 'rgba(227,67,100,0.12)',
                border: '1px solid rgba(227,67,100,0.35)',
                color: '#E34364',
                padding: '12px 14px',
                borderRadius: '10px',
                fontSize: 13,
                marginBottom: 18,
              }}
            >
              ⚠️ {error}
            </div>
          )}

          {/* Event Quick Summary Box */}
          <div
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: 20,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 12,
              fontSize: 12,
            }}
          >
            <div>
              <span style={{ color: '#7c7c9c' }}>📅 Date: </span>
              <strong style={{ color: '#fff' }}>{event.event_date}</strong>
            </div>
            <div>
              <span style={{ color: '#7c7c9c' }}>🕒 Scheduled: </span>
              <strong style={{ color: '#27C85A' }}>
                {event.scheduled_hours || event.duration_hours || 3} hrs
              </strong>
            </div>
            <div>
              <span style={{ color: '#7c7c9c' }}>👤 Client: </span>
              <span style={{ color: '#fff' }}>{event.customer?.name || 'Client'}</span>
            </div>
            <div>
              <span style={{ color: '#7c7c9c' }}>🎪 Booth: </span>
              <span style={{ color: '#fff' }}>{event.booth_type}</span>
            </div>
          </div>

          {/* Actual Hours Input */}
          <div style={{ marginBottom: 18 }}>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 700,
                color: '#fff',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Actual Hours Worked *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                step="0.25"
                min="0.5"
                max="24"
                required
                value={actualHours}
                onChange={(e) => setActualHours(e.target.value)}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(159,92,255,0.3)',
                  borderRadius: 10,
                  padding: '12px 14px',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 700,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: 14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#7c7c9c',
                  fontSize: 13,
                }}
              >
                Hours
              </span>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: '#7c7c9c' }}>
              Pre-filled with scheduled hours. Adjust if setup or extra event overtime occurred.
            </p>
          </div>

          {/* Organizer Notes / Incident Report */}
          <div style={{ marginBottom: 18 }}>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 700,
                color: '#fff',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Event Notes / Incident Report (Optional)
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Event went great, client loved the prints! Paper roll replaced once. Booth packed and in good condition."
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10,
                padding: '10px 14px',
                color: '#fff',
                fontSize: 13,
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Checklist Confirmation */}
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              cursor: 'pointer',
              marginBottom: 24,
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={checklistConfirmed}
              onChange={(e) => setChecklistConfirmed(e.target.checked)}
              style={{
                marginTop: 3,
                width: 16,
                height: 16,
                accentColor: '#27C85A',
                cursor: 'pointer',
              }}
            />
            <span style={{ fontSize: 12, color: '#a0a0b8', lineHeight: '1.4' }}>
              I confirm that all photo booth hardware has been safely packed, equipment is accounted for, and client verbal sign-off was received.
            </span>
          </label>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '10px',
                color: '#a0a0b8',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 2,
                padding: '12px',
                background: loading ? '#555' : 'linear-gradient(135deg, #27C85A 0%, #1fab47 100%)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 15px rgba(39, 200, 90, 0.4)',
              }}
            >
              {loading ? 'Submitting…' : '✓ Mark as Completed'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
